
class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('change', this.onVariantChange);
    this.variantsData = JSON.parse(this.querySelector('[type="application/json"]').textContent);
    this.init();
  }

  init() {
    // Check URL for variant
    const urlParams = new URLSearchParams(window.location.search);
    const variantId = urlParams.get('variant');
    if (variantId) {
      const variant = this.variantsData.find(v => v.id.toString() === variantId);
      if (variant) {
        this.updateOptionsInUI(variant);
      }
    }
    
    // Initial setup for impossible combinations
    this.updateOptions();
    
    // Handle back button
    window.addEventListener('popstate', () => {
      const params = new URLSearchParams(window.location.search);
      const id = params.get('variant');
      if (id) {
        const v = this.variantsData.find(v => v.id.toString() === id);
        if (v) {
          this.updateOptionsInUI(v);
          this.updateMasterId(v);
          this.updateMedia(v);
          this.updatePrice(v);
          this.updateInventory(v);
          this.updateButtons(v);
        }
      }
    });
  }

  onVariantChange() {
    this.updateOptions();
    this.updateMasterId();
    this.updateMedia();
    this.updateURL();
    this.updatePrice();
    this.updateInventory();
    this.updateButtons();
  }

  updateOptions() {
    const fieldsets = Array.from(this.querySelectorAll('.product-option-group'));
    this.options = fieldsets.map(fieldset => {
      const checkedInput = fieldset.querySelector('input:checked');
      return checkedInput ? checkedInput.value : undefined;
    });

    this.currentVariant = this.variantsData.find((variant) => {
      return !variant.options.map((option, index) => {
        return this.options[index] === option;
      }).includes(false);
    });
    
    // Fallback if combination is invalid
    if (!this.currentVariant) {
      // Find the option that was just changed
      const activeElement = document.activeElement;
      if (activeElement && activeElement.tagName === 'INPUT') {
        const changedOptionValue = activeElement.value;
        const changedOptionIndex = parseInt(activeElement.name.replace('options[', '').replace(']', '')) - 1;
        
        const fallback = this.variantsData.find(v => v.options[changedOptionIndex] === changedOptionValue && v.available) ||
                         this.variantsData.find(v => v.options[changedOptionIndex] === changedOptionValue);
                         
        if (fallback) {
          this.currentVariant = fallback;
          this.updateOptionsInUI(fallback);
        }
      }
    }

    this.updateVariantStatuses();
  }
  
  updateOptionsInUI(variant) {
    variant.options.forEach((optionValue, index) => {
      const inputs = this.querySelectorAll(`input[name="options[${index + 1}]"]`);
      inputs.forEach(input => {
        input.checked = input.value === optionValue;
      });
    });
    this.options = variant.options;
  }

  updateVariantStatuses() {
    const fieldsets = Array.from(this.querySelectorAll('.product-option-group'));
    fieldsets.forEach((fieldset, index) => {
      const inputs = Array.from(fieldset.querySelectorAll('input'));
      inputs.forEach(input => {
        const optionValue = input.value;
        // Check if this option value + other selected options equals an available variant
        const isAvailable = this.variantsData.some(variant => {
          if (variant.options[index] !== optionValue) return false;
          if (!variant.available) return false;
          
          // Must match other options
          let matchesOthers = true;
          this.options.forEach((opt, idx) => {
            if (idx !== index && opt && variant.options[idx] !== opt) {
              matchesOthers = false;
            }
          });
          return matchesOthers;
        });
        
        const label = fieldset.querySelector(`label[for="${input.id}"]`);
        if (label) {
          if (!isAvailable) {
            label.classList.add('is-unavailable');
          } else {
            label.classList.remove('is-unavailable');
          }
        }
      });
    });
  }

  updateMasterId(variant = this.currentVariant) {
    if (!variant) return;
    const idInput = document.querySelector('input[name="id"]');
    if (idInput) idInput.value = variant.id;
  }

  updateMedia(variant = this.currentVariant) {
    if (!variant || !variant.featured_media_id) return;
    const gallery = document.querySelector('product-gallery');
    if (gallery && gallery.scrollToMedia) {
      gallery.scrollToMedia(variant.featured_media_id);
    }
  }

  updateURL(variant = this.currentVariant) {
    if (!variant) return;
    window.history.replaceState({}, '', `?variant=${variant.id}`);
  }

  updatePrice(variant = this.currentVariant) {
    if (!variant) return;
    const priceEl = document.querySelector('.product-price');
    const compareEl = document.querySelector('.product-compare-price');
    
    if (priceEl && variant.price_formatted) {
      priceEl.textContent = variant.price_formatted;
    }
    
    if (compareEl) {
      if (variant.compare_at_price_formatted) {
        compareEl.textContent = variant.compare_at_price_formatted;
        compareEl.style.display = '';
      } else {
        compareEl.textContent = '';
        compareEl.style.display = 'none';
      }
    }
  }

  updateInventory(variant = this.currentVariant) {
    if (!variant) return;
    const availEl = document.querySelector('.product-availability');
    const availTextEl = availEl ? availEl.querySelector('.product-availability__text') : null;
    
    if (availEl && availTextEl) {
      if (variant.available) {
        availEl.classList.remove('product-availability--out');
        const qty = variant.inventory_quantity;
        if (qty > 0) {
          availTextEl.textContent = `${qty} available · ready to ship`;
        } else {
          availTextEl.textContent = 'In stock · ready to ship';
        }
      } else {
        availEl.classList.add('product-availability--out');
        availTextEl.textContent = 'Currently sold out';
      }
    }
  }

  updateButtons(variant = this.currentVariant) {
    const addBtn = document.querySelector('.product-add-btn');
    const buyNowBtn = document.querySelector('.product-buy-now-btn');
    const addBtnText = addBtn ? addBtn.querySelector('.btn-text') : null;
    const buyNowBtnText = buyNowBtn ? buyNowBtn.querySelector('.btn-text') : null;

    if (variant) {
      const available = variant.available;
      if (addBtn) {
        addBtn.disabled = !available;
        if (addBtnText) addBtnText.textContent = available ? 'Add to Cart' : 'Sold Out';
      }
      if (buyNowBtn) {
        buyNowBtn.disabled = !available;
        if (buyNowBtnText) buyNowBtnText.textContent = available ? 'Buy Now' : 'Sold Out';
      }
    } else {
      if (addBtn) { addBtn.disabled = true; if(addBtnText) addBtnText.textContent = 'Unavailable'; }
      if (buyNowBtn) { buyNowBtn.disabled = true; if(buyNowBtnText) buyNowBtnText.textContent = 'Unavailable'; }
    }
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductGallery extends HTMLElement {
  constructor() {
    super();
    this.slides = Array.from(this.querySelectorAll('.product-image-slide'));
    this.thumbs = Array.from(this.querySelectorAll('.product-gallery__thumb'));
    this.gallery = this.querySelector('.product-images');
    
    this.thumbs.forEach(thumb => {
      thumb.addEventListener('click', () => {
        this.scrollToIndex(parseInt(thumb.dataset.index, 10));
      });
    });
    
    if (this.gallery) {
      this.gallery.addEventListener('scroll', () => {
        const slideWidth = this.slides[0]?.getBoundingClientRect().width || 0;
        if (!slideWidth) return;
        const index = Math.round(this.gallery.scrollLeft / slideWidth);
        this.syncActiveThumb(index);
      });
    }
  }

  scrollToIndex(index) {
    if (!this.slides.length) return;
    const targetSlide = this.slides[index];
    if (targetSlide) {
      targetSlide.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
    this.syncActiveThumb(index);
  }

  scrollToMedia(mediaId) {
    const targetSlide = this.querySelector(`[data-media-id="${mediaId}"]`);
    if (targetSlide) {
      targetSlide.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      const index = this.slides.indexOf(targetSlide);
      if (index > -1) this.syncActiveThumb(index);
    }
  }

  syncActiveThumb(index) {
    this.thumbs.forEach((thumb, thumbIndex) => {
      thumb.classList.toggle('is-active', thumbIndex === index);
    });
  }
}

customElements.define('product-gallery', ProductGallery);

// Quantity Stepper Logic & AJAX Add to Cart
document.addEventListener('DOMContentLoaded', () => {
  const steppers = document.querySelectorAll('.qty-stepper');
  steppers.forEach(stepper => {
    const input = stepper.querySelector('.qty-stepper__input');
    stepper.querySelectorAll('.qty-stepper__btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const step = parseInt(btn.dataset.step, 10);
        let val = parseInt(input.value, 10) || 1;
        val = Math.max(1, val + step);
        input.value = val;
      });
    });
  });

  // AJAX Add to Cart
  const productForm = document.querySelector('.product-form form');
  if (productForm) {
    productForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      
      const btn = e.submitter && e.submitter.matches('button[type="submit"]') ? e.submitter : productForm.querySelector('.product-add-btn');
      if (!btn || btn.disabled) return;
      
      const btnText = btn.querySelector('.btn-text') || btn;
      const originalText = btnText.innerHTML;
      
      btn.disabled = true;
      btnText.textContent = 'Adding...';

      const formData = new FormData(productForm);
      const variantId = formData.get('id');
      const quantity = parseInt(formData.get('quantity') || 1, 10);
      
      // Extract line item properties (e.g. properties[Cashew Grade])
      const properties = {};
      for (const [key, value] of formData.entries()) {
        const match = key.match(/^properties\[(.+)\]$/);
        if (match && value !== '') properties[match[1]] = value;
      }
      const hasProps = Object.keys(properties).length > 0;

      try {
        if (window.CartAPI) {
          await window.CartAPI.add(variantId, quantity, hasProps ? properties : null);
        } else {
          // Fallback if CartAPI is missing
          const item = { id: variantId, quantity };
          if (hasProps) item.properties = properties;
          await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: [item] })
          });
        }
        
        if (btn.classList.contains('product-buy-now-btn')) {
          window.location.href = '/checkout';
          return;
        }
        
        btnText.textContent = 'Added!';
        setTimeout(() => {
          btnText.innerHTML = originalText;
          btn.disabled = false;
        }, 2000);
      } catch (error) {
        btnText.textContent = 'Error';
        setTimeout(() => {
          btnText.innerHTML = originalText;
          btn.disabled = false;
        }, 2000);
      }
    });
  }
});
